import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { OliveAvailabilityForm } from "@/components/OliveAvailabilityForm";
import { Leaf, MapPin, CalendarDays, UtensilsCrossed, Gift, Clock, Users, ArrowRight, Droplet } from "lucide-react";

const HIGHLIGHTS = [
  { icon: CalendarDays, label: "Quando", value: "Sabato 3 ottobre 2026", note: "Data da confermare" },
  { icon: MapPin, label: "Dove", value: "Uliveto ad Asciano", note: "Punto di ritrovo da comunicare" },
  { icon: Clock, label: "Orari", value: "In definizione", note: "Organizzazione della giornata in arrivo" },
  { icon: UtensilsCrossed, label: "Pranzo", value: "Offerto dall'associazione", note: "Per tutti i partecipanti" },
  { icon: Gift, label: "Per te", value: "Una bottiglietta d'olio", note: "A ogni volontario, quando disponibile" },
  { icon: Users, label: "Come", value: "Tutti insieme", note: "Un momento di comunità all'aperto" },
];

export default function RaccoltaOlive() {
  return (
    <Layout>
      {/* HERO */}
      <section data-testid="raccolta-hero" className="relative bg-[#1f2d22] text-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #E8C9A0 1px, transparent 0)", backgroundSize: "30px 30px" }} />
        <div className="pointer-events-none absolute -top-40 -right-24 w-[520px] h-[520px] rounded-full bg-[#6B8E3F]/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -left-24 w-[460px] h-[460px] rounded-full bg-[#C15C3D]/15 blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 mb-6">
            <Leaf className="w-3.5 h-3.5 text-[#E8C9A0]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#E8C9A0]">Raccolta delle olive · 3 ottobre 2026</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight max-w-3xl">
            Dall'oliveto recuperato, <span className="italic font-light text-[#E8C9A0]">un olio che aiuta Asciano.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl">
            Torniamo insieme tra gli ulivi per raccogliere i frutti dell'oliveto che abbiamo recuperato e produrre l'olio di Per Asciano.
          </p>
          <a href="#disponibilita" data-testid="raccolta-hero-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#C15C3D] hover:bg-[#a64830] text-white font-bold px-7 py-4 transition-colors shadow-lg">
            <Droplet className="w-5 h-5" /> Dai la tua disponibilità <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* STORY */}
      <section className="py-16 sm:py-24 bg-[#FBF9F6]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-[#C15C3D]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#C15C3D]">Il progetto</span>
          </div>
          <div className="space-y-5 text-base sm:text-lg text-[#3F3F3F] leading-relaxed">
            <p>Dopo esserci presi cura dell'oliveto, vogliamo tornare insieme tra gli ulivi per raccoglierne i frutti e produrre l'olio di Per Asciano.</p>
            <p>L'olio verrà venduto e i proventi contribuiranno a finanziare la pulizia e il recupero degli uliveti e le altre attività dell'associazione.</p>
            <p>Sarà un'occasione per lavorare insieme, stare all'aperto e condividere un momento di comunità. L'associazione offrirà il pranzo ai partecipanti e riserverà una bottiglietta dell'olio prodotto a ogni volontario, da consegnare quando sarà disponibile.</p>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="py-16 sm:py-20 bg-white border-y border-[#E5E1D8]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-display text-2xl sm:text-3xl font-black text-[#1A1A1A] mb-10">Le informazioni principali</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {HIGHLIGHTS.map((h) => {
              const Icon = h.icon;
              return (
                <div key={h.label} className="rounded-3xl border-2 border-[#E5E1D8] bg-[#FBF9F6] p-6">
                  <div className="w-11 h-11 rounded-2xl bg-[#2E4F3B]/10 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-[#2E4F3B]" /></div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C15C3D]">{h.label}</div>
                  <div className="mt-1 font-display text-lg font-black text-[#1A1A1A] leading-snug">{h.value}</div>
                  <div className="mt-1 text-sm text-[#5C5C5C]">{h.note}</div>
                </div>
              );
            })}
          </div>
          <p className="mt-8 text-sm text-[#5C5C5C] italic">Le date e i dettagli organizzativi sono ancora da confermare: lascia la tua disponibilità e ti aggiorneremo appena tutto sarà definito.</p>
        </div>
      </section>

      {/* FORM */}
      <section id="disponibilita" data-testid="raccolta-form" className="py-16 sm:py-24 bg-[#1f2d22]">
        <div className="max-w-2xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-8">
            <h2 className="font-display text-3xl sm:text-4xl font-black text-white leading-tight">Vieni a raccogliere le olive con noi</h2>
            <p className="mt-3 text-white/80">Indica quando puoi esserci. È una raccolta preliminare di disponibilità.</p>
          </div>
          <OliveAvailabilityForm variant="light" />
          <div className="mt-8 text-center">
            <Link to="/" className="inline-flex items-center gap-2 text-[#E8C9A0] font-bold hover:gap-3 transition-all">
              Torna alla home <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
